import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Home, LogOut, Plus, Menu, LogIn, UserPlus } from "lucide-react";
import { isNativeApp } from "@/lib/platform";
import NotificationBell from "@/components/mobile/NotificationBell";

export default function Navbar() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    const navLink = (to, label, testId) => {
        const active = location.pathname === to;
        return (
            <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    active
                        ? "bg-foreground text-background"
                        : "text-foreground/70 hover:text-foreground hover:bg-muted"
                }`}
            >
                {label}
            </Link>
        );
    };

    const mobileLink = (to, label, testId) => {
        const active = location.pathname === to;
        return (
            <Link
                key={to}
                to={to}
                data-testid={`mobile-${testId}`}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-2xl text-base font-medium transition-all ${
                    active
                        ? "bg-foreground text-background"
                        : "text-foreground hover:bg-muted"
                }`}
            >
                {label}
            </Link>
        );
    };

    const doLogout = () => {
        setMenuOpen(false);
        logout();
    };

    return (
        <header className="sticky top-0 z-50 bg-background border-b border-border/60" style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link
                    to={user && isNativeApp() ? "/onboarding" : "/"}
                    data-testid="nav-logo"
                    className="flex items-center gap-2"
                >
                    <div className="w-9 h-9 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                        <Home className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <span className="font-display text-xl font-bold tracking-tight">
                        Erasmus<span className="text-primary">Swap</span>
                    </span>
                </Link>

                {/* Desktop navigation */}
                <nav className="hidden md:flex items-center gap-1">
                    {navLink("/browse", "Browse", "nav-browse")}
                    {!user && navLink("/exchange-buddy", "Find your Exchange Buddy", "nav-exchange-buddy")}
                    {user && navLink("/matches", "Matches", "nav-matches")}
                    {user && navLink("/messages", "Messages", "nav-messages")}
                    {user && navLink("/dashboard", "Dashboard", "nav-dashboard")}
                    {user && user.is_admin && navLink("/admin", "Admin", "nav-admin")}
                </nav>

                {/* Desktop action buttons */}
                <div className="hidden md:flex items-center gap-2">
                    {user ? (
                        <>
                            <Button
                                data-testid="nav-create-listing-btn"
                                onClick={() => navigate("/listings/new")}
                                className="rounded-full bg-primary hover:bg-primary/90"
                            >
                                <Plus className="w-4 h-4 mr-1" /> List room
                            </Button>
                            <Button
                                data-testid="nav-logout-btn"
                                variant="ghost"
                                onClick={logout}
                                className="rounded-full"
                            >
                                <LogOut className="w-4 h-4" />
                                <span className="ml-1">Log out</span>
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                data-testid="nav-login-btn"
                                variant="ghost"
                                onClick={() => navigate("/login")}
                                className="rounded-full"
                            >
                                Log in
                            </Button>
                            <Button
                                data-testid="nav-register-btn"
                                onClick={() => navigate("/register")}
                                className="rounded-full bg-primary hover:bg-primary/90"
                            >
                                Sign up
                            </Button>
                        </>
                    )}
                </div>

                {/* Notification bell — Capacitor app only, replaces the hamburger slot */}
                {isNativeApp() && (
                    <div className="md:hidden flex items-center">
                        <NotificationBell />
                    </div>
                )}

                {/* Mobile hamburger — hidden inside the Capacitor app (bottom tab bar covers everything);
                    still visible on the web mobile site where users don't have a bottom nav. */}
                {!isNativeApp() && (
                    <div className="md:hidden flex items-center gap-2">
                        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="nav-mobile-menu-btn"
                                className="w-10 h-10 rounded-full border border-border/70 flex items-center justify-center hover:bg-muted"
                                aria-label="Open menu"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[85%] max-w-sm p-6 flex flex-col">
                            <SheetTitle className="sr-only">Menu</SheetTitle>
                            <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
                            <div className="flex items-center gap-2 pb-4 border-b border-border/60">
                                <div className="w-9 h-9 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                                    <Home className="w-5 h-5" strokeWidth={2.5} />
                                </div>
                                <span className="font-display text-lg font-bold tracking-tight">
                                    Erasmus<span className="text-primary">Swap</span>
                                </span>
                            </div>

                            <nav className="flex-1 flex flex-col gap-1 mt-4" data-testid="mobile-nav-links">
                                {mobileLink("/browse", "Browse", "nav-browse")}
                                {!user && mobileLink("/exchange-buddy", "Find your Exchange Buddy", "nav-exchange-buddy")}
                                {user && mobileLink("/matches", "Matches", "nav-matches")}
                                {user && mobileLink("/messages", "Messages", "nav-messages")}
                                {user && mobileLink("/dashboard", "Dashboard", "nav-dashboard")}
                                {user && user.is_admin && mobileLink("/admin", "Admin", "nav-admin")}
                            </nav>

                            <div className="border-t border-border/60 pt-4 mt-4 flex flex-col gap-2">
                                {user ? (
                                    <>
                                        <div className="px-4 py-2 text-xs text-muted-foreground truncate">
                                            Signed in as <span className="font-medium text-foreground">{user.email}</span>
                                        </div>
                                        <Button
                                            data-testid="mobile-nav-listing-btn"
                                            onClick={() => { setMenuOpen(false); navigate("/listings/new"); }}
                                            className="w-full rounded-full h-11 bg-primary hover:bg-primary/90"
                                        >
                                            <Plus className="w-4 h-4 mr-1" /> List a room
                                        </Button>
                                        <Button
                                            data-testid="mobile-nav-logout-btn"
                                            variant="outline"
                                            onClick={doLogout}
                                            className="w-full rounded-full h-11"
                                        >
                                            <LogOut className="w-4 h-4 mr-1" /> Log out
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            data-testid="mobile-nav-register-btn"
                                            onClick={() => { setMenuOpen(false); navigate("/register"); }}
                                            className="w-full rounded-full h-11 bg-primary hover:bg-primary/90"
                                        >
                                            <UserPlus className="w-4 h-4 mr-1" /> Sign up free
                                        </Button>
                                        <Button
                                            data-testid="mobile-nav-login-btn"
                                            variant="outline"
                                            onClick={() => { setMenuOpen(false); navigate("/login"); }}
                                            className="w-full rounded-full h-11"
                                        >
                                            <LogIn className="w-4 h-4 mr-1" /> Log in
                                        </Button>
                                    </>
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>
                    </div>
                )}
            </div>
        </header>
    );
}
